import {
  AuthService,
  CaptchaService,
  ServerService,
} from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { UserFriendlyError } from '@affine/error';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { useSelfhostLoginVersionGuard } from '../hooks/affine/use-selfhost-login-version-guard';
import { Captcha } from '../sign-in/captcha';
import * as styles from './login.css';

export function GoldllenLoginForm({
  onAuthenticated,
}: {
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) {
  const auth = useService(AuthService);
  const captcha = useService(CaptchaService);
  const server = useService(ServerService);
  const versionError = useSelfhostLoginVersionGuard(server.server);
  const loginStatus = useLiveData(auth.session.status$);
  const needCaptcha = useLiveData(captcha.needCaptcha$);
  const verifyToken = useLiveData(captcha.verifyToken$);
  const challenge = useLiveData(captcha.challenge$);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const authenticated = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const id = useId();

  useEffect(() => {
    if (loginStatus === 'authenticated' && !authenticated.current) {
      authenticated.current = true;
      onAuthenticated?.(loginStatus);
    }
  }, [loginStatus, onAuthenticated]);

  const login = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting.current || versionError || (needCaptcha && !verifyToken))
        return;

      const email = emailRef.current?.value.trim() ?? '';
      const password = passwordRef.current?.value ?? '';
      // An empty password invokes the backend's magic-link flow. Never submit it.
      if (!email || !password) {
        setError('Informe seu e-mail e sua senha para entrar.');
        return;
      }
      if (!emailRef.current?.validity.valid) {
        setError('Informe um e-mail válido.');
        return;
      }

      submitting.current = true;
      setBusy(true);
      setError('');
      try {
        await auth.signInPassword({ email, password, verifyToken, challenge });
      } catch (cause) {
        const failure = UserFriendlyError.fromAny(cause);
        setError(
          failure.is('WRONG_SIGN_IN_CREDENTIALS') ||
            failure.is('PASSWORD_REQUIRED')
            ? 'E-mail ou senha incorretos. Confira os dados e tente novamente.'
            : failure.is('REQUEST_ABORTED') || failure.is('NETWORK_ERROR')
              ? 'Não foi possível conectar. Verifique sua conexão e tente novamente.'
              : 'Não foi possível entrar. Tente novamente ou fale com o administrador.'
        );
        captcha.revalidate();
      } finally {
        submitting.current = false;
        setBusy(false);
      }
    },
    [auth, captcha, challenge, needCaptcha, verifyToken, versionError]
  );

  return (
    <div className={styles.formContent}>
      <div>
        <h1 className={styles.heading}>Entrar</h1>
        <p className={styles.description}>
          Acesse o Goldllen com seu e-mail e sua senha.
        </p>
      </div>
      <form
        className={styles.form}
        onSubmit={event => {
          login(event).catch(() =>
            setError('Não foi possível entrar. Tente novamente.')
          );
        }}
        aria-busy={busy}
      >
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${id}-email`}>
            E-mail
          </label>
          <input
            className={styles.input}
            id={`${id}-email`}
            ref={emailRef}
            name="username"
            type="email"
            placeholder="seu@email.com"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            readOnly={busy}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            data-testid="email-input"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${id}-password`}>
            Senha
          </label>
          <input
            className={styles.input}
            id={`${id}-password`}
            ref={passwordRef}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            readOnly={busy}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            data-testid="password-input"
          />
        </div>
        {versionError}
        {needCaptcha && <Captcha />}
        <button
          className={styles.submit}
          type="submit"
          disabled={busy || !!versionError || (needCaptcha && !verifyToken)}
          aria-busy={busy}
          data-testid="sign-in-button"
        >
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <p className={styles.error} id={`${id}-error`} role="alert">
          {error}
        </p>
      </form>
      <p className={styles.footer}>
        Primeiro acesso? Solicite ao administrador seu link para definir a
        senha.
      </p>
    </div>
  );
}
